import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import {useState} from "react";
import DuprPartnerBadge from "../components/DuprPartnerBadge.tsx";
import {GeneratorProgress, GeneratorTopCta, useStagedGenerate} from "../components/generatorReveal.tsx";
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";
import TournamentManagementCta from "../landing/sections/TournamentManagementCta.tsx";

export const Route = createFileRoute('/mexicano-tournament-generator')({
    component: RouteComponent,
})

type MexMatch = {teamA: string; teamB: string};
type MexRound = {title: string; matches: MexMatch[]; resting: string[]};

/** Rotating doubles: each round rotates the player order, then pairs (p1+p4) vs (p2+p3) per court of four. */
function buildMexicano(players: string[], rounds: number): MexRound[] {
    const result: MexRound[] = [];
    for (let round = 1; round <= rounds; round++) {
        const rotated = [...players];
        for (let i = 0; i < round - 1; i++) rotated.push(rotated.shift() as string);

        const usableCount = rotated.length - (rotated.length % 4);
        const usable = rotated.slice(0, usableCount);
        const resting = rotated.slice(usableCount);

        const matches: MexMatch[] = [];
        for (let i = 0; i < usable.length; i += 4) {
            matches.push({teamA: `${usable[i]} & ${usable[i + 3]}`, teamB: `${usable[i + 1]} & ${usable[i + 2]}`});
        }
        result.push({title: `Round ${round}`, matches, resting});
    }
    return result;
}

const STEPS = [
    {title: "Add Players", description: "Enter all players taking part in your Mexicano tournament."},
    {title: "Rotate Matchups", description: "Generate doubles matches with changing partners and opponents."},
    {title: "Adapt By Results", description: "Later rounds can be adjusted based on scores and rankings."},
];

const VS_ROWS = [
    {feature: "Pairing style", mex: "Can adapt based on results", ame: "Usually fixed rotation"},
    {feature: "Best for", mex: "Balanced competitive social events", ame: "Simple rotating club events"},
    {feature: "Common sports", mex: "Padel, tennis, pickleball", ame: "Padel, tennis, pickleball"},
    {feature: "Complexity", mex: "Medium", ame: "Low"},
    {feature: "Player experience", mex: "More balanced later rounds", ame: "Very easy to understand"},
];

const BEST_FOR = ["Padel club nights", "Tennis doubles events", "Pickleball socials", "Mixed-ability groups", "Balanced social competition"];
const LESS_IDEAL = ["Traditional knockout tournaments", "Formal league standings", "Singles competitions", "Events where everyone must play everyone", "Very large tournaments with strict brackets"];

const FAQS = [
    {
        q: 'What is a Mexicano tournament?',
        a: <>A Mexicano tournament is a rotating doubles format where players change partners and opponents across rounds. Later rounds can be based on scores or rankings.</>,
    },
    {
        q: 'How many players do you need for Mexicano?',
        a: <>Mexicano works best with multiples of four, because each match is doubles. With extra players, you can rotate players in and out between rounds.</>,
    },
    {
        q: 'Is Mexicano good for padel?',
        a: <>Yes. Mexicano is especially popular for padel club events because it creates fun, balanced doubles matches. To manage the full event — registration, entry-fee payments, draws, live scoring and standings — use ScorePal's <Link to={"/padel-tournament-software/" as any} className="text-[#082F8A] underline font-semibold">padel tournament software</Link>.</>,
    },
    {
        q: 'What is the difference between Americano and Mexicano?',
        a: <>Americano usually follows a fixed rotation, while Mexicano can adapt pairings based on player results, rankings or performance. Try the <Link to={"/americano-generator/" as any} className="text-[#082F8A] underline font-semibold">Americano generator</Link> for the fixed-rotation version.</>,
    },
    {
        q: 'Can ScorePal manage Mexicano scores?',
        a: <>Yes. ScorePal manages fixtures, live scores, rankings, standings and player communication for Mexicano events.</>,
    },
];

function RouteComponent() {
    const [playersText, setPlayersText] = useState("");
    const [rounds, setRounds] = useState("4");
    const [schedule, setSchedule] = useState<MexRound[] | null>(null);
    const [summary, setSummary] = useState<{players: number; rounds: number; perRound: number; restPerRound: number} | null>(null);
    const [error, setError] = useState<string | null>(null);

    const gen = useStagedGenerate(["Seeding the opening round", "Matching by standings", "Laying out the rounds"]);

    const generate = () => {
        const players = playersText.split("\n").map((n) => n.trim()).filter(Boolean);
        if (players.length < 4) {
            setError("Please enter at least four players.");
            setSchedule(null);
            setSummary(null);
            return;
        }
        const roundCount = Math.min(12, Math.max(1, parseInt(rounds, 10) || 4));
        setError(null);
        setSchedule(null);
        setSummary(null);
        gen.run(() => {
            setSchedule(buildMexicano(players, roundCount));
            setSummary({
                players: players.length,
                rounds: roundCount,
                perRound: Math.floor(players.length / 4),
                restPerRound: players.length % 4,
            });
        });
    };

    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <title>Mexicano Tournament Generator (Free) | ScorePal</title>
            <meta name="description" content="Create a Mexicano tournament schedule instantly. Generate rotating doubles fixtures for padel, tennis, pickleball and club events with ScorePal's free Mexicano tournament generator." />
            <link rel="canonical" href={`${config.publicUrl}/mexicano-tournament-generator/`} />
            <meta property="og:title" content="Mexicano Tournament Generator | ScorePal" />
            <meta property="og:description" content="Generate Mexicano tournament fixtures for padel, tennis, pickleball and social club events." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`${config.publicUrl}/mexicano-tournament-generator/`} />
            <meta property="og:image" content="https://scorepal.co/images/mexicano-tournament-generator-og.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Mexicano Tournament Generator | ScorePal" />
            <meta name="twitter:description" content="Create Mexicano tournament schedules instantly with ScorePal." />

            <JsonLd id={`page${Route.id}/app`} item={{
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "Mexicano Tournament Generator",
                "applicationCategory": "SportsApplication",
                "operatingSystem": "Web",
                "description": "A free online tool to generate Mexicano tournament fixtures for padel, tennis, pickleball and club events.",
                "url": "https://scorepal.co/mexicano-tournament-generator/",
                "publisher": {"@type": "Organization", "name": "ScorePal"},
                "offers": {"@type": "Offer", "price": "0", "priceCurrency": "USD"},
            }} />
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                    {"@type": "Question", "name": "What is a Mexicano tournament?", "acceptedAnswer": {"@type": "Answer", "text": "A Mexicano tournament is a rotating doubles format where players change partners and opponents, often with later rounds based on player results or rankings."}},
                    {"@type": "Question", "name": "What sports use Mexicano tournaments?", "acceptedAnswer": {"@type": "Answer", "text": "Mexicano tournaments are popular in padel, tennis, pickleball and other doubles-based club sports."}},
                    {"@type": "Question", "name": "What is the difference between Americano and Mexicano?", "acceptedAnswer": {"@type": "Answer", "text": "Americano usually follows a fixed rotation, while Mexicano can adapt pairings based on player results, rankings or performance."}},
                    {"@type": "Question", "name": "How many players do you need for Mexicano?", "acceptedAnswer": {"@type": "Answer", "text": "Mexicano works best with multiples of four players because each match is doubles. Extra players can rotate in and out between rounds."}},
                    {"@type": "Question", "name": "Can ScorePal manage Mexicano scores?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal manages fixtures, live scores, rankings, standings and player communication for Mexicano events."}},
                ],
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Mexicano Tournament Generator", item: "https://scorepal.co/mexicano-tournament-generator/"}]} />

            {/* Hero + generator */}
            <section className="relative overflow-hidden p-4">
                <DuprPartnerBadge />
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <div>
                                <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-5">
                                    Free Club Tournament Tool
                                </span>
                                <h1 className="font-bebas text-white uppercase text-[clamp(40px,6vw,72px)] leading-none font-bold tracking-wide mb-5">
                                    Mexicano Tournament Generator
                                </h1>
                                <p className="font-inter text-white/85 text-base lg:text-xl max-w-2xl leading-relaxed mb-8">
                                    Create a Mexicano tournament schedule for padel, tennis, pickleball and doubles-based club
                                    events. Generate rotating matchups where players change partners and opponents across rounds.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-white transition">
                                        <span className="font-inter text-base font-medium">Create Tournament</span>
                                        <div className="bg-[#082F8A] rounded-full p-1">
                                            <ArrowUpRightIcon className="text-white" size={28} />
                                        </div>
                                    </Link>
                                    <Link to={"/tournament-formats/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                        Compare Formats
                                    </Link>
                                </div>
                            </div>

                            {/* Generator card */}
                            <div id="generator" className="bg-white rounded-3xl p-6 lg:p-8 shadow-xl scroll-mt-24">
                                <h2 className="font-bebas text-[#1a1a2e] uppercase text-3xl font-bold tracking-wide mb-1">Create Your Mexicano</h2>
                                <p className="font-inter text-[#1a1a2e]/60 text-sm mb-5">Enter your players and choose how many rounds to play.</p>

                                <label htmlFor="players" className="block font-inter font-semibold text-[#1a1a2e] mb-2">Players</label>
                                <textarea
                                    id="players"
                                    value={playersText}
                                    onChange={(e) => setPlayersText(e.target.value)}
                                    placeholder={"Enter one player per line\nPlayer 1\nPlayer 2\nPlayer 3\nPlayer 4\nPlayer 5\nPlayer 6\nPlayer 7\nPlayer 8"}
                                    className="w-full min-h-[150px] resize-y rounded-xl border border-[#e5e7eb] p-4 font-inter text-[#1a1a2e] focus:outline-none focus:ring-2 focus:ring-[#082F8A]/40"
                                />

                                <label htmlFor="rounds" className="block font-inter font-semibold text-[#1a1a2e] mt-4 mb-2">Number of rounds</label>
                                <input
                                    id="rounds"
                                    type="number"
                                    min={1}
                                    max={12}
                                    value={rounds}
                                    onChange={(e) => setRounds(e.target.value)}
                                    className="w-full rounded-xl border border-[#e5e7eb] p-3 font-inter text-[#1a1a2e] focus:outline-none focus:ring-2 focus:ring-[#082F8A]/40"
                                />

                                <button
                                    type="button"
                                    onClick={generate}
                                    disabled={gen.running}
                                    className="mt-5 w-full rounded-xl bg-[#082F8A] py-3.5 font-inter font-bold text-white transition hover:bg-[#06246b] disabled:opacity-70"
                                >
                                    {gen.running ? "Generating…" : "Generate Mexicano Schedule"}
                                </button>

                                <GeneratorProgress gen={gen} />

                                {error && <p className="mt-3 font-inter text-sm text-red-600">{error}</p>}
                                {summary && (
                                    <p className="mt-4 font-inter text-sm text-[#1a1a2e]/70">
                                        <strong>{summary.players}</strong> players · <strong>{summary.rounds}</strong> rounds ·{" "}
                                        <strong>{summary.perRound}</strong> matches per round
                                        {summary.restPerRound ? <> · <strong>{summary.restPerRound}</strong> rotate out per round</> : null}
                                    </p>
                                )}

                                {schedule && <GeneratorTopCta note="Run it live on ScorePal with scores, a leaderboard and a shareable page." />}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Results */}
            {schedule && (
                <section className="py-12 lg:py-16 bg-white">
                    <div className="container mx-auto px-6 lg:px-12">
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-2">
                            Your Mexicano Schedule
                        </h2>
                        <p className="font-inter text-[#1a1a2e]/70 mb-8">Use these as a starting schedule, then adapt later rounds based on player scores.</p>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {schedule.map((round) => (
                                <div key={round.title} className="bg-[#F6F7FB] border border-[#e5e7eb] rounded-2xl p-5">
                                    <div className="font-bebas text-xl text-[#082F8A] font-bold mb-3">{round.title}</div>
                                    <div className="space-y-2.5">
                                        {round.matches.map((m, i) => (
                                            <div key={i} className="bg-white border border-[#e5e7eb] rounded-xl p-3 font-inter text-sm">
                                                <div className="font-semibold text-[#1a1a2e]">{m.teamA}</div>
                                                <div className="text-[#1a1a2e]/40 my-0.5">vs</div>
                                                <div className="font-semibold text-[#1a1a2e]">{m.teamB}</div>
                                            </div>
                                        ))}
                                    </div>
                                    {round.resting.length > 0 && (
                                        <p className="mt-3 font-inter text-xs text-[#1a1a2e]/60">
                                            <strong>Resting:</strong> {round.resting.join(", ")}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="mt-10 flex justify-center">
                            <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-[#082F8A] text-white rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-[#06246b] transition">
                                <span className="font-inter text-base font-medium">Run this tournament with ScorePal</span>
                                <div className="bg-white rounded-full p-1">
                                    <ArrowUpRightIcon className="text-[#082F8A]" size={28} />
                                </div>
                            </Link>
                        </div>
                    </div>
                </section>
            )}

            {/* What is Mexicano / steps */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                        What Is a Mexicano Tournament?
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed max-w-4xl">
                        A Mexicano tournament is a rotating doubles format commonly used in padel, tennis and pickleball clubs.
                        Players rotate partners and opponents, with later rounds often adjusted so players face others with
                        similar results.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
                        {STEPS.map((step, index) => (
                            <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                                <div className="w-10 h-10 rounded-full bg-[#EDEEF4] text-[#082F8A] flex items-center justify-center font-bold mb-4">{index + 1}</div>
                                <h3 className="font-inter text-[#082F8A] font-semibold text-base mb-2">{step.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{step.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Mexicano vs Americano */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Mexicano vs Americano
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-8">
                        Mexicano and <Link to={"/americano-generator/" as any} className="text-[#082F8A] underline font-semibold">Americano</Link> are similar social doubles formats, but they are not exactly the same.
                    </p>
                    <div className="overflow-x-auto">
                        <div className="min-w-[640px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-3 bg-[#F6F7FB] font-bebas text-lg">
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Feature</div>
                                <div className="p-5 border-r border-[#d9dce7] text-[#082F8A]">Mexicano</div>
                                <div className="p-5 text-[#1a1a2e]">Americano</div>
                            </div>
                            {VS_ROWS.map((row, i) => (
                                <div key={row.feature} className={`grid grid-cols-3 border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]/80">{row.feature}</div>
                                    <div className="p-5 border-r border-[#d9dce7] font-medium">{row.mex}</div>
                                    <div className="p-5 text-[#1a1a2e]/80">{row.ame}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* When to use */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-8">
                        When Should You Use a Mexicano Format?
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="font-inter font-semibold text-[#12b76a] mb-3">Best For</h3>
                            <ul className="list-disc pl-5 space-y-2 font-inter text-sm text-[#1a1a2e]/80">
                                {BEST_FOR.map((x) => <li key={x}>{x}</li>)}
                            </ul>
                        </div>
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="font-inter font-semibold text-[#d92d20] mb-3">Less Ideal For</h3>
                            <ul className="list-disc pl-5 space-y-2 font-inter text-sm text-[#1a1a2e]/80">
                                {LESS_IDEAL.map((x) => <li key={x}>{x}</li>)}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
                        Run Your Mexicano Tournament With ScorePal
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
                        Generate fixtures, rotate players, track scores, manage rankings and publish live results from one simple tournament platform.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2.5 mb-10">
                        {["Mexicano", "Americano", "Padel", "Tennis", "Pickleball", "Live Scores"].map((pill) => (
                            <span key={pill} className="rounded-full bg-white/10 px-4 py-2 font-inter text-sm font-medium text-white/90">{pill}</span>
                        ))}
                    </div>
                    <div className="flex justify-center">
                        <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Get started - it's free</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"Mexicano Tournament FAQs"} faqs={FAQS} />
            <TournamentManagementCta />
            <FooterSection />
        </div>
    );
}
